import React, { useState } from 'react';

// ─── Colour palette per generation (cool → warm) ──────────────────────────────
const GEN_COLORS = [
  '#0ea5e9', // gen 1 — sky blue (present generation)
  '#6366f1', // gen 2 — indigo
  '#8b5cf6', // gen 3 — violet
  '#a855f7', // gen 4 — purple
  '#ec4899', // gen 5 — pink
  '#f97316', // gen 6 — orange
  '#16a34a', // gen 7 — green (seventh generation)
];

// ─── GenCard ──────────────────────────────────────────────────────────────────
function GenCard({ phase, color, isExpanded, onToggle, onEdit, readonly }) {
  const hasContent =
    phase.goals.length || phase.actions.length || phase.risks.length || phase.indicators.length;

  return (
    <div
      style={{
        border: `2px solid ${color}`,
        borderRadius: 10,
        background: isExpanded ? '#0f172a' : '#1e293b',
        flex: '0 0 200px',
        minWidth: 180,
        cursor: 'pointer',
        transition: 'background 0.15s',
      }}
    >
      {/* Header */}
      <div
        onClick={onToggle}
        style={{
          background: color,
          padding: '10px 12px',
          borderRadius: '7px 7px 0 0',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <span style={{ fontWeight: 700, color: '#fff', fontSize: 13 }}>
          Gen {phase.generation}
        </span>
        {phase.label && (
          <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>{phase.label}</span>
        )}
        {phase.timeframe && (
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>{phase.timeframe}</span>
        )}
      </div>

      {/* Collapsed summary */}
      {!isExpanded && (
        <div style={{ padding: '10px 12px' }}>
          {!hasContent && (
            <span style={{ color: '#64748b', fontSize: 11, fontStyle: 'italic' }}>
              No content yet
            </span>
          )}
          {phase.goals.length > 0 && (
            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              {phase.goals.length} goal{phase.goals.length > 1 ? 's' : ''}
            </div>
          )}
          {phase.actions.length > 0 && (
            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              {phase.actions.length} action{phase.actions.length > 1 ? 's' : ''}
            </div>
          )}
          {phase.risks.length > 0 && (
            <div style={{ fontSize: 11, color: '#f87171' }}>
              ⚠ {phase.risks.length} risk{phase.risks.length > 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}

      {/* Expanded detail */}
      {isExpanded && (
        <div style={{ padding: '10px 12px' }}>
          <Section label="Goals" items={phase.goals} color={color} />
          <Section label="Actions" items={phase.actions} color={color} />
          <Section label="Risks" items={phase.risks} color="#f87171" />
          <Section label="Indicators" items={phase.indicators} color="#94a3b8" />
          {phase.assumptions && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>ASSUMPTIONS</div>
              <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>{phase.assumptions}</p>
            </div>
          )}
          {!readonly && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(phase.generation); }}
              style={{
                marginTop: 10,
                background: 'transparent',
                border: `1px solid ${color}`,
                color,
                borderRadius: 5,
                padding: '4px 10px',
                fontSize: 11,
                cursor: 'pointer',
                width: '100%',
              }}
            >
              Edit
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ label, items, color }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 10, color, fontWeight: 700, marginBottom: 3, letterSpacing: 1 }}>
        {label.toUpperCase()}
      </div>
      <ul style={{ margin: 0, padding: '0 0 0 14px' }}>
        {items.map((item, i) => (
          <li key={i} style={{ fontSize: 11, color: '#cbd5e1', marginBottom: 2 }}>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Phase Editor Modal ────────────────────────────────────────────────────────
function PhaseEditor({ phase, color, onSave, onClose }) {
  const [form, setForm] = useState({
    label:       phase.label || '',
    goals:       phase.goals.join('\n'),
    actions:     phase.actions.join('\n'),
    risks:       phase.risks.join('\n'),
    indicators:  phase.indicators.join('\n'),
    assumptions: phase.assumptions || '',
  });

  const splitLines = (str) => str.split('\n').map(s => s.trim()).filter(Boolean);

  const handleSave = () => {
    onSave(phase.generation, {
      label:       form.label,
      goals:       splitLines(form.goals),
      actions:     splitLines(form.actions),
      risks:       splitLines(form.risks),
      indicators:  splitLines(form.indicators),
      assumptions: form.assumptions,
    });
  };

  const fieldStyle = {
    width: '100%',
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 6,
    color: '#e2e8f0',
    padding: '7px 10px',
    fontSize: 13,
    boxSizing: 'border-box',
    marginBottom: 12,
  };
  const labelStyle = { fontSize: 11, color: '#94a3b8', marginBottom: 4, fontWeight: 600 };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#0f172a', borderRadius: 12, padding: 24,
          width: 520, maxHeight: '85vh', overflowY: 'auto',
          borderTop: `4px solid ${color}`,
        }}
        onClick={e => e.stopPropagation()}
      >
        <h3 style={{ color: '#f1f5f9', margin: '0 0 4px' }}>
          Generation {phase.generation} — {phase.timeframe}
        </h3>
        <p style={{ color: '#64748b', fontSize: 12, margin: '0 0 16px' }}>
          One item per line for goals, actions, risks, and indicators.
        </p>

        <div style={labelStyle}>Label</div>
        <input
          value={form.label}
          onChange={e => setForm({ ...form, label: e.target.value })}
          placeholder={`e.g. "Our grandchildren"`}
          style={fieldStyle}
        />

        <div style={labelStyle}>Goals</div>
        <textarea
          rows={3}
          value={form.goals}
          onChange={e => setForm({ ...form, goals: e.target.value })}
          placeholder="One goal per line"
          style={fieldStyle}
        />

        <div style={labelStyle}>Actions</div>
        <textarea
          rows={3}
          value={form.actions}
          onChange={e => setForm({ ...form, actions: e.target.value })}
          placeholder="One action per line"
          style={fieldStyle}
        />

        <div style={labelStyle}>Risks</div>
        <textarea
          rows={2}
          value={form.risks}
          onChange={e => setForm({ ...form, risks: e.target.value })}
          placeholder="One risk per line"
          style={fieldStyle}
        />

        <div style={labelStyle}>Indicators</div>
        <textarea
          rows={2}
          value={form.indicators}
          onChange={e => setForm({ ...form, indicators: e.target.value })}
          placeholder="How we'll measure success (one per line)"
          style={fieldStyle}
        />

        <div style={labelStyle}>Assumptions</div>
        <textarea
          rows={2}
          value={form.assumptions}
          onChange={e => setForm({ ...form, assumptions: e.target.value })}
          placeholder="Key assumptions underlying this generation's plan"
          style={fieldStyle}
        />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <button onClick={onClose}
            style={{ background: '#1e293b', border: '1px solid #334155', color: '#94a3b8',
              borderRadius: 6, padding: '7px 16px', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSave}
            style={{ background: color, border: 'none', color: '#fff',
              borderRadius: 6, padding: '7px 16px', cursor: 'pointer', fontWeight: 600 }}>
            Save Generation
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── GenerationTimeline ───────────────────────────────────────────────────────
/**
 * Props:
 *   phases      — array of phase objects from SevenGenPlan.phases
 *   readonly    — boolean; hides edit buttons
 *   onPhaseSave — (genNumber, updatedFields) => void  (only needed when !readonly)
 */
function GenerationTimeline({ phases = [], readonly = false, onPhaseSave }) {
  const [expandedGen, setExpandedGen] = useState(null);
  const [editingGen,  setEditingGen]  = useState(null);

  const editingPhase = editingGen
    ? phases.find(p => p.generation === editingGen)
    : null;

  const toggleExpand = (gen) => setExpandedGen(g => (g === gen ? null : gen));

  return (
    <div>
      {/* Horizontal scroll track */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          overflowX: 'auto',
          paddingBottom: 12,
          alignItems: 'flex-start',
        }}
      >
        {phases.map((phase) => {
          const color = GEN_COLORS[(phase.generation - 1) % GEN_COLORS.length];
          return (
            <GenCard
              key={phase.generation}
              phase={phase}
              color={color}
              isExpanded={expandedGen === phase.generation}
              onToggle={() => toggleExpand(phase.generation)}
              onEdit={setEditingGen}
              readonly={readonly}
            />
          );
        })}
      </div>

      <p style={{ fontSize: 11, color: '#475569', margin: '4px 0 0' }}>
        Click any generation to expand · Each generation spans ~25 years
      </p>

      {editingPhase && (
        <PhaseEditor
          phase={editingPhase}
          color={GEN_COLORS[(editingPhase.generation - 1) % GEN_COLORS.length]}
          onSave={(gen, fields) => {
            onPhaseSave(gen, fields);
            setEditingGen(null);
          }}
          onClose={() => setEditingGen(null)}
        />
      )}
    </div>
  );
}

export default GenerationTimeline;
